import PropTypes from 'prop-types';

import {
    Box,
    Button,
    Heading,
    IconSummaryPanel,
    Link,
    IconSummaryPanelList,
    PayIcon,
    NewTabIcon,
    MeterIcon,
    Text as PText,
    PageRow,
    Paragraph,
    PoundPlusMinusIcon,
    ThumbsUpIcon
} from '@cof/plastic-components';
import { css, styled } from 'styled-components';

import CreditWiseSvg from '../../../../assets/shared/svg/logo_creditwise.svg?react';
import { withText } from '../../../../text/textStore';
import MoneyHelperLogo from '../../../MoneyHelperBanner/MoneyHelperLogo';

const CreditWiseLogoStyled = styled(CreditWiseSvg)`
    height: 5.821rem;
    width: 14.65rem;
    margin: 0 auto;
    display: block;

    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        height: 6.611rem;
        width: 16.742rem;
    }
`;

const MoneyHelperLogoStyled = styled(MoneyHelperLogo)`
    height: 6.5rem;
    width: 12.9rem;
    margin: 0 auto;
    display: block;

    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        height: 6.7rem;
        width: 13.5rem;
    }
`;

const iconStyles = css`
    height: 4rem;
    width: 4rem;
    max-width: 95%;

    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        height: 6rem;
        width: 6rem;
    }
`;

const PoundPlusMinusIconStyled = styled(PoundPlusMinusIcon)`
    ${iconStyles}
`;

const ThumbsUpIconStyled = styled(ThumbsUpIcon)`
    ${iconStyles}
`;

const MeterIconStyled = styled(MeterIcon)`
    ${iconStyles}
`;

const PayIconStyled = styled(PayIcon)`
    ${iconStyles}
`;

const CreditWiseWhatCanYouDoWrapper = styled(Box)`
    ${({ theme }: any) => theme.components.CreditWiseWhatCanYouDoWrapper}
`;

const CustomPageRow = styled(PageRow)`
    ${({ theme }: any) => css`
        padding: ${theme.space.lg} ${theme.space.sm};

        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.xl} 0;
            width: 95%;
        }
    `}
`;

const CenteredParagraph = styled.div`
    ${({ theme }: any) => css`
        width: 100%;
        margin: 0 auto;
        text-align: center;
        font-size: large;
        padding-bottom: ${theme.space.lg};

        ${theme.breakpoints.mq.tabletMin} {
            width: 80%;
            font-size: x-large;
        }
    `}
`;

const StyledHeading = styled.div`
    ${({ theme }: any) => css`
        width: 100%;
        margin: 0 auto;
        padding: 0 0 ${theme.space.md} 0;

        ${theme.breakpoints.mq.tabletMin} {
            padding: 0 0 ${theme.space.lg} 0;
        }
    `}
`;

const StyledIconSummaryPanelList = styled(IconSummaryPanelList)`
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    ${({ theme }: any) => theme.breakpoints.mq.tabletMin} {
        flex-direction: row;
        flex-wrap: wrap;
    }

    div {
        max-width: none;
    }
`;

const StyledWhatWeLookAtPanel = styled(IconSummaryPanel)`
    ${({ theme }: any) => css`
        background-color: ${theme.colors.global.white};
        align-items: center;
        display: flex;
        margin-right: 0;
        margin-bottom: ${theme.space.xs};
        padding: ${theme.space.sm};

        &:last-child {
            margin-bottom: 0;
        }

        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.lg};
            margin-right: ${theme.space.sm};
            margin-bottom: 0;

            &:last-child {
                margin-right: 0;
            }
        }
    `}
`;

const StyledWhatCanYouDoNextPanel = styled(IconSummaryPanel)`
    ${({ theme }: any) => css`
        display: block;
        margin: 0 0 ${theme.space.sm} 0;
        padding: ${theme.space.md} ${theme.space.sm};
        border-radius: 8px;

        &:last-child {
            margin-bottom: 0;
        }

        ${theme.breakpoints.mq.tabletMin} {
            width: 80%;
            margin: 0 ${theme.space.sm} 0 0;
            padding: ${theme.space.lg};

            &:last-child {
                margin-right: 0;
            }
        }
    `}
`;

const CreditWiseHeadingWithPadding = styled(Heading)`
    ${({ theme }: any) => css`
        padding: 0 0 ${theme.space.xs} 0;

        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.sm} 0 ${theme.space.xs} 0;
        }
    `}
`;

const MoneyHelperHeadingWithPadding = styled(Heading)`
    ${({ theme }: any) => css`
        padding: 0 0 ${theme.space.xs} 0;

        ${theme.breakpoints.mq.tabletMin} {
            padding: ${theme.space.sm} 0 ${theme.space.xs} 0;
        }
    `}
`;

const StyledLink = styled(Link)`
    ${({ theme }: any) => css`
        font-size: large;

        ${theme.breakpoints.mq.tabletMin} {
            font-size: x-large;
        }
    `}
`;

const CreditWiseSection = ({ Text, Text: text, JsxText }: any) => {
    return (
        <>
            <CustomPageRow wrapperBg={'global.grey50'} maxWidth="110rem">
                <Box data-qa-id="how-did-we-decide-this">
                    <Heading
                        data-qm-allow="true"
                        level="2"
                        fontWeight={'bold'}
                        data-qa-id="heading"
                        pb={['xs', null, 'sm']}
                    >
                        <Text data-qm-allow="true" path="HowDidWeDecideThis.Heading" />
                    </Heading>
                    <CenteredParagraph data-qm-allow="true" as={Paragraph}>
                        <JsxText
                            path="HowDidWeDecideThis.Body.Copy"
                            params={{
                                EQUIFAX: (
                                    <StyledLink
                                        external
                                        href={text({ path: 'HowDidWeDecideThis.Body.Equifax.Target' })}
                                    >
                                        <Text path="HowDidWeDecideThis.Body.Equifax.Text" />
                                    </StyledLink>
                                ),
                                EXPERIAN: (
                                    <StyledLink
                                        external
                                        href={text({ path: 'HowDidWeDecideThis.Body.Experian.Target' })}
                                    >
                                        <Text path="HowDidWeDecideThis.Body.Experian.Text" />
                                    </StyledLink>
                                )
                            }}
                        />
                    </CenteredParagraph>
                    <StyledHeading data-qm-allow="true" as={Heading} level="3" data-qa-id="heading">
                        <Text path="WhatWeLookAt.Heading" />
                    </StyledHeading>
                    <StyledIconSummaryPanelList colorVariant="dark" mobileLayoutVariant="condensed">
                        <StyledWhatWeLookAtPanel Icon={PoundPlusMinusIconStyled} data-qa-id="panel-1">
                            <PText data-qm-allow="true">{text({ path: 'WhatWeLookAt.Points.Point1.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                        <StyledWhatWeLookAtPanel Icon={ThumbsUpIconStyled} data-qa-id="panel-2">
                            <PText data-qm-allow="true">{text({ path: 'WhatWeLookAt.Points.Point2.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                        <StyledWhatWeLookAtPanel Icon={MeterIconStyled} data-qa-id="panel-3">
                            <PText data-qm-allow="true">{text({ path: 'WhatWeLookAt.Points.Point3.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                        <StyledWhatWeLookAtPanel Icon={PayIconStyled} data-qa-id="panel-4">
                            <PText data-qm-allow="true">{text({ path: 'WhatWeLookAt.Points.Point4.Body' })}</PText>
                        </StyledWhatWeLookAtPanel>
                    </StyledIconSummaryPanelList>
                </Box>
            </CustomPageRow>
            <CustomPageRow maxWidth="110rem">
                <CreditWiseWhatCanYouDoWrapper>
                    <Heading
                        data-qm-allow="true"
                        level="2"
                        fontWeight={'bold'}
                        data-qa-id="heading"
                        pb={['md', null, 'lg']}
                    >
                        <Text path="WhatCanYouDo.Heading" />
                    </Heading>
                </CreditWiseWhatCanYouDoWrapper>
                <StyledIconSummaryPanelList colorVariant="dark" mobileLayoutVariant="condensed">
                    {/* @ts-expect-error TS2769: No overload matches this call. */}
                    <StyledWhatCanYouDoNextPanel Icon={CreditWiseLogoStyled} data-qa-id="creditwise-panel">
                        <CreditWiseHeadingWithPadding data-qm-allow="true" level="3" data-qa-id="heading">
                            <Text path="WhatCanYouDo.CreditScore.Heading" />
                        </CreditWiseHeadingWithPadding>
                        <Paragraph data-qm-allow="true" data-qa-id="body" pb={'md'} textAlign={'center'}>
                            <Text path="WhatCanYouDo.CreditScore.Body" />
                        </Paragraph>
                        <Button
                            // @ts-expect-error TS2322: Type ' children: Element[]; target: string; as: "a"; href: any; "data-qa-id": string; rel: string; '
                            target="_blank"
                            as="a"
                            href={text({ path: 'WhatCanYouDo.CreditScore.Button.GuideLink' })}
                            data-qa-id="get-creditwise-button"
                            rel="noopener noreferrer"
                        >
                            <Text data-qm-allow="true" path="WhatCanYouDo.CreditScore.Button.Text" />
                            <NewTabIcon
                                // @ts-expect-error TS2322: Type ' role: string; verticalAlign: string; ml: "0.5rem"; pb: "0.3rem"; "aria-label": string; ' is n
                                role="img"
                                verticalAlign="middle"
                                ml="0.5rem"
                                pb={'0.3rem'}
                                aria-label=". This link opens in a new tab"
                            />
                        </Button>
                    </StyledWhatCanYouDoNextPanel>
                    <StyledWhatCanYouDoNextPanel Icon={MoneyHelperLogoStyled} data-qa-id="moneyhelper-panel">
                        <MoneyHelperHeadingWithPadding data-qm-allow="true" level="3" data-qa-id="heading">
                            <Text path="WhatCanYouDo.MoneyHelperCapOne.Heading" />
                        </MoneyHelperHeadingWithPadding>
                        <Paragraph data-qm-allow="true" data-qa-id="body" pb={'md'} textAlign={'center'}>
                            <Text path="WhatCanYouDo.MoneyHelperCapOne.Body" />
                        </Paragraph>
                        <Button
                            // @ts-expect-error TS2322: Type ' children: Element[]; target: string; as: "a"; href: any; "data-qa-id": string; rel: string; '
                            target="_blank"
                            as="a"
                            href={text({ path: 'WhatCanYouDo.MoneyHelperCapOne.Button.GuideLink' })}
                            data-qa-id="get-moneyhelper-button"
                            rel="noopener noreferrer"
                        >
                            <Text data-qm-allow="true" path="WhatCanYouDo.MoneyHelperCapOne.Button.Text" />
                            <NewTabIcon
                                // @ts-expect-error TS2322: Type ' role: string; verticalAlign: string; ml: "0.5rem"; pb: "0.3rem"; "aria-label": string; ' is n
                                role="img"
                                verticalAlign="middle"
                                ml="0.5rem"
                                pb={'0.3rem'}
                                aria-label=". This link opens in a new tab"
                            />
                        </Button>
                    </StyledWhatCanYouDoNextPanel>
                </StyledIconSummaryPanelList>
            </CustomPageRow>
        </>
    );
};

CreditWiseSection.propTypes = {
    Text: PropTypes.func.isRequired,
    JsxText: PropTypes.func.isRequired
};

const WrappedCreditWiseSection = withText('ScoreDecline')(CreditWiseSection);

WrappedCreditWiseSection.displayName = 'CreditWiseSection';

export default WrappedCreditWiseSection;
